/**
 * ジョブ進捗の購読フック（DESIGN §9 の useJobStream・audit M-6 の配線）。
 * サーバー実装済みの SSE `/api/jobs/:id/stream` を消費し、
 * SSE が張れない環境では 2 秒ポーリング（GET /api/jobs/:id）へフォールバックする。
 * ポーリングが連続で失敗し続けたら止めて `lost` を立てる（無限ポーリング・永久スピナー防止 — audit C-L）。
 */

import { useEffect, useState } from 'react'
import { apiClient } from '../lib/api'

export interface JobStreamState {
  status: 'queued' | 'running' | 'completed' | 'failed' | 'canceled'
  progress?: { stage?: string } | null
  error?: string | null
  /** 進捗を取得できなくなった（ジョブ自体は続いている可能性がある）。このときは購読を止めている */
  lost?: boolean
}

/** ポーリングの連続失敗がこの回数に達したら諦める */
export const MAX_POLL_ERRORS = 5

export function isTerminalJobStatus(s: string): boolean {
  return s === 'completed' || s === 'failed' || s === 'canceled'
}

export function useJobStream(jobId: string | null): JobStreamState | null {
  const [state, setState] = useState<JobStreamState | null>(null)

  useEffect(() => {
    if (!jobId) { setState(null); return }
    setState({ status: 'queued' })
    let closed = false
    let pollTimer: ReturnType<typeof setInterval> | null = null
    let pollErrors = 0

    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
    }

    const onPollError = (message: string) => {
      pollErrors += 1
      if (pollErrors >= MAX_POLL_ERRORS) {
        stopPolling()
        setState(s => ({ ...(s ?? { status: 'queued' }), lost: true, error: message }))
      }
    }

    const startPolling = () => {
      if (pollTimer || closed) return
      pollTimer = setInterval(() => {
        // async の reject を interval の外へ漏らさない（未処理の Promise 拒否を防ぐ）
        void (async () => {
          try {
            const res = await apiClient.get<JobStreamState>(`/jobs/${jobId}`)
            if (closed) return
            if (res.success && res.data) {
              pollErrors = 0
              setState({ status: res.data.status, progress: res.data.progress, error: res.data.error })
              if (isTerminalJobStatus(res.data.status)) stopPolling()
            } else {
              onPollError(res.error || '進捗を取得できませんでした')
            }
          } catch (e) {
            if (closed) return
            onPollError(e instanceof Error ? e.message : '進捗を取得できませんでした')
          }
        })()
      }, 2000)
    }

    const es = new EventSource(`/api/jobs/${jobId}/stream`)
    es.onmessage = (ev) => {
      if (closed) return
      try {
        const data = JSON.parse(ev.data) as JobStreamState
        setState(data)
        if (isTerminalJobStatus(data.status)) es.close()
      } catch {
        // 壊れたイベントは無視（次のイベントで回復）
      }
    }
    es.onerror = () => {
      // SSE が張れない/切れた → ポーリングへフォールバック
      es.close()
      startPolling()
    }

    return () => {
      closed = true
      es.close()
      stopPolling()
    }
  }, [jobId])

  return state
}

export const JOB_STAGE_LABEL: Record<string, string> = {
  writing: '本文を執筆中…',
  image: '画像を生成中…',
  guard: 'コンプラチェック中…',
  publish: '投稿処理中…',
  done: '完了',
}
